import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  ClauseStatusDetail,
  ClauseStatusFilters,
  ClauseStatusValue,
  ComplianceEvidence,
  CreateEvidencePayload,
  AttachRecordPayload,
  AuditClauseResponse,
  CorrectiveAction,
  PaginatedResponse,
} from '@/lib/types';

const BASE = '/api/v1/compliance/clauses';
const FIVE_MINUTES = 1000 * 60 * 5;

function cleanFilters(filters: Record<string, string | number | undefined>): Record<string, string> {
  const cleaned: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = String(value);
    }
  }
  return cleaned;
}

const CLAUSE_PAGE_SIZE = 100;

export function useClauseStatuses(filters: ClauseStatusFilters = {}) {
  const cleaned = cleanFilters(filters as Record<string, string | number | undefined>);
  // Pagination is handled internally so the full clause register is always
  // fetched, regardless of the caller's page_size hint.
  delete cleaned.page;
  delete cleaned.page_size;

  return useQuery({
    queryKey: queryKeys.clauseStatus.list(cleaned),
    queryFn: async (): Promise<ClauseStatusDetail[]> => {
      const all: ClauseStatusDetail[] = [];
      let page = 1;
      while (true) {
        const response = await apiClient.get<
          PaginatedResponse<ClauseStatusDetail> | ClauseStatusDetail[]
        >(`${BASE}/`, {
          params: {
            ...cleaned,
            page_size: String(CLAUSE_PAGE_SIZE),
            page: String(page),
          },
        });
        const data = response.data;
        if (Array.isArray(data)) return data;
        all.push(...data.results);
        if (!data.next) break;
        page += 1;
      }
      return all;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useClauseStatusDetail(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.clauseStatus.detail(id),
    queryFn: async (): Promise<ClauseStatusDetail> => {
      const response = await apiClient.get<ClauseStatusDetail>(`${BASE}/${id}/`);
      return response.data;
    },
    enabled,
  });
}

export function useClauseEvidence(clauseStatusId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.clauseStatus.evidence(clauseStatusId),
    queryFn: async (): Promise<ComplianceEvidence[]> => {
      const response = await apiClient.get<
        { results: ComplianceEvidence[] } | ComplianceEvidence[]
      >(`${BASE}/${clauseStatusId}/evidence/`);
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray((data as { results: ComplianceEvidence[] }).results))
        return (data as { results: ComplianceEvidence[] }).results;
      return [];
    },
    enabled,
    staleTime: FIVE_MINUTES,
  });
}

export function useClauseAuditHistory(clauseRef: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.clauseStatus.auditHistory(clauseRef),
    queryFn: async (): Promise<AuditClauseResponse[]> => {
      const response = await apiClient.get<AuditClauseResponse[]>(
        '/api/v1/compliance/audit-prep/clause-responses/',
        { params: { clause_ref: clauseRef } },
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled,
    staleTime: FIVE_MINUTES,
  });
}

export function useClauseActions(clauseStatusId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.clauseStatus.actions(clauseStatusId),
    queryFn: async (): Promise<CorrectiveAction[]> => {
      const response = await apiClient.get<
        PaginatedResponse<CorrectiveAction>
      >('/api/v1/compliance/actions/', {
        params: { clause_status: String(clauseStatusId) },
      });
      const data = response.data;
      if (Array.isArray(data)) return data as unknown as CorrectiveAction[];
      return data.results ?? [];
    },
    enabled,
    staleTime: FIVE_MINUTES,
  });
}

export function useUpdateClauseStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: { status?: ClauseStatusValue; next_review_due?: string | null };
    }): Promise<ClauseStatusDetail> => {
      const response = await apiClient.patch<ClauseStatusDetail>(
        `${BASE}/${id}/`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
    },
  });
}

export function useCreateEvidence(clauseStatusId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEvidencePayload): Promise<ComplianceEvidence> => {
      const formData = new FormData();
      formData.append('evidence_type', payload.evidence_type);
      formData.append('title', payload.title);
      if (payload.description) formData.append('description', payload.description);
      if (payload.file) formData.append('file', payload.file);
      if (payload.external_url) formData.append('external_url', payload.external_url);
      if (payload.valid_from) formData.append('valid_from', payload.valid_from);
      if (payload.valid_until) formData.append('valid_until', payload.valid_until);

      const response = await apiClient.post<ComplianceEvidence>(
        `${BASE}/${clauseStatusId}/evidence/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.clauseStatus.evidence(clauseStatusId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
    },
  });
}

export function useAttachRecord(clauseStatusId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AttachRecordPayload): Promise<ComplianceEvidence> => {
      const response = await apiClient.post<ComplianceEvidence>(
        `${BASE}/${clauseStatusId}/evidence/attach-record/`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.clauseStatus.evidence(clauseStatusId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
    },
  });
}

export function useConfirmEvidence(clauseStatusId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (evidenceId: number): Promise<ComplianceEvidence> => {
      const response = await apiClient.post<ComplianceEvidence>(
        `${BASE}/${clauseStatusId}/evidence/${evidenceId}/confirm/`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.clauseStatus.evidence(clauseStatusId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
    },
  });
}

export function useDismissEvidence(clauseStatusId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (evidenceId: number): Promise<void> => {
      await apiClient.post(
        `${BASE}/${clauseStatusId}/evidence/${evidenceId}/dismiss/`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.clauseStatus.evidence(clauseStatusId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
    },
  });
}
